export interface ColorStyle {
  className: string;
  style: React.CSSProperties;
}

export interface TierColorStyle extends ColorStyle {
  textColor: string;
  contrastRatio: number;
}

export const ColorUtils = {
  /**
   * Convert hex color to RGB values
   */
  hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: Number.parseInt(result[1], 16),
          g: Number.parseInt(result[2], 16),
          b: Number.parseInt(result[3], 16),
        }
      : null;
  },

  /**
   * Calculate relative luminance of a color (0-1 scale)
   * Based on WCAG 2.1 guidelines
   */
  getLuminance(r: number, g: number, b: number): number {
    const [rs, gs, bs] = [r, g, b].map((c) => {
      const sRGB = c / 255;
      return sRGB <= 0.03928 ? sRGB / 12.92 : ((sRGB + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  },

  /**
   * Calculate contrast ratio between two colors
   * Returns value between 1 and 21
   */
  getContrastRatio(
    color1: { r: number; g: number; b: number },
    color2: { r: number; g: number; b: number }
  ): number {
    const lum1 = ColorUtils.getLuminance(color1.r, color1.g, color1.b);
    const lum2 = ColorUtils.getLuminance(color2.r, color2.g, color2.b);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    return (brightest + 0.05) / (darkest + 0.05);
  },

  /**
   * Get the best contrasting text color (black or white) for a background
   */
  getContrastingTextColor(backgroundColor: string): {
    color: string;
    contrastRatio: number;
  } {
    let bgRgb: { r: number; g: number; b: number } | null = null;

    // Try to parse as hex
    if (backgroundColor.startsWith("#")) {
      bgRgb = ColorUtils.hexToRgb(backgroundColor);
    }

    // Fallback to default CSS colors if hex parsing fails
    if (!bgRgb) {
      const colorMap: Record<string, { r: number; g: number; b: number }> = {
        gray: { r: 128, g: 128, b: 128 },
        grey: { r: 128, g: 128, b: 128 },
        black: { r: 0, g: 0, b: 0 },
        white: { r: 255, g: 255, b: 255 },
        red: { r: 255, g: 0, b: 0 },
        green: { r: 0, g: 128, b: 0 },
        blue: { r: 0, g: 0, b: 255 },
        yellow: { r: 255, g: 255, b: 0 },
        purple: { r: 128, g: 0, b: 128 },
        orange: { r: 255, g: 165, b: 0 },
        pink: { r: 255, g: 192, b: 203 },
      };

      const colorName = backgroundColor
        .toLowerCase()
        .replace(/^(bg-)?/, "")
        .split("-")[0];
      bgRgb = colorMap[colorName] || { r: 128, g: 128, b: 128 }; // Default to gray
    }

    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };

    const whiteContrast = ColorUtils.getContrastRatio(bgRgb, white);
    const blackContrast = ColorUtils.getContrastRatio(bgRgb, black);

    // Return the color with better contrast
    if (whiteContrast > blackContrast) {
      return { color: "#ffffff", contrastRatio: whiteContrast };
    }
    return { color: "#000000", contrastRatio: blackContrast };
  },

  /**
   * Get color style with enhanced accessibility for tier badges
   */
  getTierColorStyle(color: string | null | undefined, opacity: number = 0.25): TierColorStyle {
    if (!color) {
      return {
        className: "bg-gray-400",
        style: {},
        textColor: "#000000",
        contrastRatio: 4.5,
      };
    }

    const backgroundColor = color;
    const contrastInfo = ColorUtils.getContrastingTextColor(backgroundColor);

    // Create background with opacity
    let backgroundWithOpacity: string;
    if (color.startsWith("#")) {
      const rgb = ColorUtils.hexToRgb(color);
      if (rgb) {
        backgroundWithOpacity = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
      } else {
        backgroundWithOpacity = `${color}${Math.round(opacity * 255)
          .toString(16)
          .padStart(2, "0")}`;
      }
    } else {
      // For named colors, we'll use CSS custom properties or fallback
      backgroundWithOpacity = color;
    }

    // If contrast is poor, increase opacity or use alternative approach
    const minContrastRatio = 4.5; // WCAG AA standard
    let finalOpacity = opacity;
    let finalTextColor = contrastInfo.color;

    if (contrastInfo.contrastRatio < minContrastRatio) {
      // Increase background opacity for better contrast
      finalOpacity = Math.min(0.6, opacity + 0.2);

      if (color.startsWith("#")) {
        const rgb = ColorUtils.hexToRgb(color);
        if (rgb) {
          backgroundWithOpacity = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${finalOpacity})`;
        }
      }

      // Use a more aggressive text color approach for poor contrast backgrounds
      const bgLuminance = color.startsWith("#")
        ? (() => {
            const rgb = ColorUtils.hexToRgb(color);
            return rgb ? ColorUtils.getLuminance(rgb.r, rgb.g, rgb.b) : 0.5;
          })()
        : 0.5; // Default assumption for non-hex colors

      finalTextColor = bgLuminance > 0.5 ? "#000000" : "#ffffff";
    }

    return {
      className: "",
      style: {
        backgroundColor: backgroundWithOpacity,
        color: finalTextColor,
        borderColor: color,
      },
      textColor: finalTextColor,
      contrastRatio: contrastInfo.contrastRatio,
    };
  },

  getColorStyle(color: string | null | undefined): ColorStyle {
    if (!color) {
      return {
        className: "bg-gray-400",
        style: {},
      };
    }

    // If it's a hex color, use inline styles
    if (color.startsWith("#")) {
      return {
        className: "",
        style: { backgroundColor: color },
      };
    }

    // If it's a named color or Tailwind class, use className
    const tailwindColors = [
      "red",
      "blue",
      "green",
      "yellow",
      "purple",
      "pink",
      "indigo",
      "cyan",
      "teal",
      "orange",
      "lime",
      "emerald",
      "sky",
      "violet",
      "rose",
      "amber",
      "gray",
      "slate",
      "zinc",
      "neutral",
      "stone",
    ];

    const isNamedColor = tailwindColors.some((twColor) => color.includes(twColor));

    if (isNamedColor && color.startsWith("bg-")) {
      // Already a Tailwind background class
      return {
        className: color,
        style: {},
      };
    }
    if (isNamedColor) {
      // Convert to Tailwind background class
      return {
        className: `bg-${color}`,
        style: {},
      };
    }

    // For CSS color names or other formats, use inline styles
    return {
      className: "",
      style: { backgroundColor: color },
    };
  },
};
